import type {
    ApiSmChaseReward,
    ApiSmChaseSnapshot,
    ApiSmSpinChaseResult,
} from "@bridge-applications/pb-types";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import symbolSwimmingTrunks from "../../../../assets/static/images/symbols/v1/symbol-swimming-trunks.webp";
import symbolCoconutDrink from "../../../../assets/static/images/symbols/v1/symbol-coconut-drink.webp";
import symbolSunglasses from "../../../../assets/static/images/symbols/v1/symbol-sunglasses.webp";
import symbolBeachBall from "../../../../assets/static/images/symbols/v1/symbol-beach-ball.webp";
import symbolStarfish from "../../../../assets/static/images/symbols/v1/symbol-starfish.webp";
import symbolShell from "../../../../assets/static/images/symbols/v1/symbol-shell.webp";
import symbolGoldenBeachCoins from "../../../../assets/static/images/symbols/v1/symbol-golden-beach-coins.webp";
import symbolEnergyDrink from "../../../../assets/static/images/symbols/v1/symbol-energy-drink.webp";
import symbolGoldenShell from "../../../../assets/static/images/symbols/v1/symbol-golden-shell.webp";
import symbolThief from "../../../../assets/static/images/symbols/v1/symbol-thief.webp";
import symbolFriendSteal from "../../../../assets/static/images/symbols/v1/symbol-friend-steal.webp";
import beachCoinsIcon from "../../../../assets/static/images/symbol-beach-coins.webp";
import "./SymbolChase.scss";

const SYMBOL_IMAGES: Readonly<Record<string, string>> = {
    "swimming-trunks": symbolSwimmingTrunks,
    "coconut-drink": symbolCoconutDrink,
    sunglasses: symbolSunglasses,
    "beach-ball": symbolBeachBall,
    starfish: symbolStarfish,
    shell: symbolShell,
    "golden-beach-coins": symbolGoldenBeachCoins,
    "energy-drink": symbolEnergyDrink,
    "golden-shell": symbolGoldenShell,
    thief: symbolThief,
    "friend-steal": symbolFriendSteal,
};

function compactInteger(value: string, locale: string): string {
    return new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(BigInt(value));
}

function rewardLabel(
    reward: ApiSmChaseReward,
    t: TFunction,
    locale: string,
): string {
    if (reward.kind === "SM_ENERGY") {
        return t("symbolChase.reward.energy", {
            amount: compactInteger(reward.amount, locale),
        });
    }
    return t("symbolChase.reward.currency", {
        amount: compactInteger(reward.amount, locale),
        currency: reward.currencyCode.replace(/_/g, " ").toLowerCase(),
    });
}

function progressPercent(pointsValue: string, targetValue: string | undefined) {
    if (!targetValue) return 100;
    const points = BigInt(pointsValue);
    const target = BigInt(targetValue);
    const clamped = points > target ? target : points;
    return Number((clamped * 10_000n) / target) / 100;
}

function rewardIcon(reward: ApiSmChaseReward | undefined) {
    if (!reward) return undefined;
    if (reward.kind === "SM_ENERGY") return symbolEnergyDrink;
    if (reward.currencyCode === "BEACH_COINS") return beachCoinsIcon;
    return undefined;
}

interface SymbolChaseProps {
    chase: ApiSmChaseSnapshot;
    notice: ApiSmSpinChaseResult | null;
}

export function SymbolChase({ chase, notice }: SymbolChaseProps) {
    const { t, i18n } = useTranslation();
    const locale = i18n.resolvedLanguage ?? i18n.language ?? "en";
    const nextMilestone = chase.milestones.find(
        (milestone) => milestone.state === "PENDING",
    );
    const rewardMilestone =
        nextMilestone ?? chase.milestones[chase.milestones.length - 1];
    const targetIcon = SYMBOL_IMAGES[chase.symbolCode];
    const nextRewardIcon = rewardIcon(rewardMilestone?.reward);
    const progress = progressPercent(
        chase.points,
        nextMilestone?.requiredPoints,
    );
    const progressTarget =
        nextMilestone?.requiredPoints ??
        rewardMilestone?.requiredPoints ??
        chase.points;
    const noticeText = notice?.milestoneGrants.length
        ? t("symbolChase.notice.unlocked", {
              rewards: notice.milestoneGrants
                  .map((grant) => rewardLabel(grant.reward, t, locale))
                  .join(" · "),
          })
        : null;

    return (
        <section
            className="slot-machine__chase"
            data-symbol-code={chase.symbolCode}
            aria-label={t("symbolChase.label", {
                name: chase.displayName,
            })}
        >
            {noticeText && (
                <div className="slot-machine__chase__notice" role="status">
                    {noticeText}
                </div>
            )}

            <div className="slot-machine__chase__indicator" dir="ltr">
                {targetIcon ? (
                    <img
                        className="slot-machine__chase__target"
                        src={targetIcon}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                    />
                ) : (
                    <span className="slot-machine__chase__target slot-machine__chase__target--fallback">
                        ?
                    </span>
                )}

                <div
                    className="slot-machine__chase__track"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(progress)}
                    aria-label={t("symbolChase.progress", {
                        name: chase.displayName,
                        points: chase.points,
                        target: progressTarget,
                        reward: rewardMilestone
                            ? rewardLabel(rewardMilestone.reward, t, locale)
                            : t("symbolChase.nextReward"),
                    })}
                >
                    <span
                        className="slot-machine__chase__fill"
                        style={{ width: `${progress}%` }}
                    />
                    <span
                        className="slot-machine__chase__glare"
                        aria-hidden="true"
                    />
                    <span className="slot-machine__chase__value" dir="ltr">
                        {compactInteger(chase.points, locale)}/
                        {compactInteger(progressTarget, locale)}
                    </span>
                </div>

                <div
                    className="slot-machine__chase__reward"
                    aria-label={
                        rewardMilestone
                            ? rewardLabel(rewardMilestone.reward, t, locale)
                            : t("symbolChase.noReward")
                    }
                >
                    {nextRewardIcon ? (
                        <img
                            src={nextRewardIcon}
                            alt=""
                            aria-hidden="true"
                            draggable={false}
                        />
                    ) : (
                        <span className="slot-machine__chase__reward-code">
                            {rewardMilestone?.reward.kind === "CURRENCY"
                                ? rewardMilestone.reward.currencyCode.slice(
                                      0,
                                      3,
                                  )
                                : "?"}
                        </span>
                    )}
                    <strong dir="ltr">
                        {rewardMilestone
                            ? "+" +
                              compactInteger(
                                  rewardMilestone.reward.amount,
                                  locale,
                              )
                            : "—"}
                    </strong>
                </div>
            </div>
        </section>
    );
}
